import React from 'react'
import { Box, Typography, Tooltip } from '@mui/material'
import { Info } from '@mui/icons-material'
import { PasswordStrength } from '@utils/validation'

interface PasswordStrengthMeterProps {
  strength: PasswordStrength
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  strength,
}) => {
  // Generate helpful tips based on strength level
  const getPasswordTips = (): string => {
    switch (strength.level) {
      case 'very-weak':
        return 'Use at least 8 characters with a mix of uppercase, lowercase, numbers, and special characters.'
      case 'weak':
        return 'Make it longer (12+ characters) and include uppercase, lowercase, numbers, and special characters. Avoid common words and sequences.'
      case 'so-so':
        return 'Good start! Add more characters (12+ recommended) and ensure you have uppercase, lowercase, numbers, and special characters.'
      case 'good':
        return 'Almost there! Consider making it longer (16+ characters) or adding more character variety for maximum security.'
      case 'strong':
        return "Excellent! Your password meets industry standards. Keep it unique and don't reuse it elsewhere."
      default:
        return 'Use a mix of uppercase, lowercase, numbers, and special characters. Minimum 8 characters, 12+ recommended.'
    }
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Strength Meter Bar */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          flex: 1,
        }}
      >
        {[1, 2, 3, 4].map(segment => (
          <Box
            key={segment}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 1,
              bgcolor:
                segment <= strength.segments ? strength.color : 'grey.300',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </Box>

      {/* Strength Label and Info Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 'fit-content',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: strength.color,
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {strength.label}
        </Typography>
        <Tooltip title={getPasswordTips()}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'grey.800',
                fontSize: '0.75rem',
                maxWidth: '280px',
                padding: '8px 12px',
                '& .MuiTooltip-arrow': {
                  color: 'grey.800',
                },
              },
            },
          }}
        >
          <Info
            sx={{
              fontSize: '1rem',
              color: strength.color,
              cursor: 'help',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          />
        </Tooltip>
      </Box>
    </Box>
  )
}

export default PasswordStrengthMeter
